const SDK_VERSION = '1.4.0'
const MESSAGE_PREFIX = 'actify:widget:'
const DEFAULT_BUTTON_SIZE = 58
const DEFAULT_MARGIN = 22
const DEFAULT_PRIMARY_COLOR = '#2C88F1'
const DEFAULT_PANEL_WIDTH = 420
const DEFAULT_PANEL_HEIGHT = 680
const DEFAULT_PANEL_MIN_WIDTH = 380
const DEFAULT_PANEL_MIN_HEIGHT = 620
const MAX_CONTEXT_BYTES = 16 * 1024
const MAX_CLIENT_TOOL_ID_LENGTH = 128
const MAX_CLIENT_TOOL_DESCRIPTION_LENGTH = 4_000
const MAX_CLIENT_TOOL_SCHEMA_BYTES = 32 * 1024
const MAX_CLIENT_TOOL_RESULT_BYTES = 50 * 1024
const MIN_CLIENT_TOOL_TIMEOUT_MS = 1_000
const MAX_CLIENT_TOOL_TIMEOUT_MS = 5 * 60_000
const DEFAULT_CLIENT_TOOL_TIMEOUT_MS = 60_000
const CLIENT_TOOL_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_-]{0,63}$/
const BLOCKED_CONTEXT_KEYS = new Set(['__proto__', 'prototype', 'constructor'])

// TOKEN_REFRESH_HELPERS_START
const DEFAULT_TOKEN_REFRESH_INTERVAL_SECONDS = 4 * 60
const DEFAULT_TOKEN_REFRESH_LEEWAY_SECONDS = 60
const DEFAULT_TOKEN_REFRESH_RETRY_SECONDS = 15
const MIN_TOKEN_REFRESH_DELAY_MS = 1000

function parsePositiveSeconds(value, fallback, minimum = 1, maximum = 24 * 60 * 60) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback
  return Math.min(Math.max(parsed, minimum), maximum)
}

function decodeJwtPayload(token) {
  if (typeof token !== 'string') return null
  const segments = token.split('.')
  if (segments.length !== 3 || !segments[1]) return null
  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const bytes = Uint8Array.from(atob(padded), character => character.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes))
  } catch {
    return null
  }
}

function getJwtExpiresAt(token) {
  const expiresAtSeconds = Number(decodeJwtPayload(token)?.exp)
  return Number.isFinite(expiresAtSeconds) && expiresAtSeconds > 0
    ? expiresAtSeconds * 1000
    : null
}

function parseExpiresAt(value) {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 10_000_000_000 ? value : value * 1000
  }
  const parsed = Date.parse(String(value))
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeTokenResult(result, now = Date.now()) {
  const token = typeof result === 'string'
    ? result.trim()
    : typeof result?.token === 'string'
      ? result.token.trim()
      : ''
  const explicitExpiresAt = typeof result === 'object' && result
    ? parseExpiresAt(result.expiresAt)
    : null
  const expiresInSeconds = typeof result === 'object' && result
    ? Number(result.expiresIn)
    : Number.NaN
  const relativeExpiresAt = Number.isFinite(expiresInSeconds) && expiresInSeconds > 0
    ? now + expiresInSeconds * 1000
    : null
  return {
    token,
    expiresAt: explicitExpiresAt || relativeExpiresAt || getJwtExpiresAt(token)
  }
}

function getTokenRefreshDelay({ expiresAt, leewaySeconds, intervalSeconds, now = Date.now() }) {
  if (Number.isFinite(expiresAt)) {
    const remainingMs = expiresAt - now
    const requestedLeewayMs = parsePositiveSeconds(
      leewaySeconds,
      DEFAULT_TOKEN_REFRESH_LEEWAY_SECONDS,
      1,
      60 * 60
    ) * 1000
    // Short-lived tokens should still spend at least half of their lifetime in use.
    const leewayMs = Math.min(requestedLeewayMs, Math.max(remainingMs / 2, 0))
    return Math.max(remainingMs - leewayMs, MIN_TOKEN_REFRESH_DELAY_MS)
  }
  return parsePositiveSeconds(
    intervalSeconds,
    DEFAULT_TOKEN_REFRESH_INTERVAL_SECONDS,
    30,
    24 * 60 * 60
  ) * 1000
}

function safeAuthErrorEvent(code, error, message) {
  const status = Number(error?.response?.status ?? error?.status)
  return {
    code,
    message,
    ...(Number.isInteger(status) && status >= 400 && status <= 599 ? { status } : {})
  }
}
// TOKEN_REFRESH_HELPERS_END

const CHAT_ICON = `
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <path d="M7.5 8.25h17a3.25 3.25 0 0 1 3.25 3.25v8.25A3.25 3.25 0 0 1 24.5 23H16l-6.65 4.1c-.7.43-1.6-.07-1.55-.9l.2-3.3a3.25 3.25 0 0 1-3.75-3.15V11.5A3.25 3.25 0 0 1 7.5 8.25Z" fill="currentColor"/>
    <path d="M10.3 15.55h11.4M10.3 19h7.35" fill="none" stroke="white" stroke-width="1.7" stroke-linecap="round"/>
    <path d="M20.7 4.8v4.1M18.65 6.85h4.1" fill="none" stroke="white" stroke-width="1.7" stroke-linecap="round"/>
  </svg>`

const CLOSE_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m6.5 6.5 11 11m0-11-11 11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`

const STYLES = `
  :host {
    --actify-primary: ${DEFAULT_PRIMARY_COLOR};
    --actify-ink: #10231f;
    --actify-paper: #f7faf8;
    --actify-line: rgba(16, 35, 31, .12);
    --actify-muted: #49625c;
    --actify-hover: rgba(16, 35, 31, .07);
    --actify-badge-ring: #fff;
    --actify-z: 2147483000;
    all: initial;
    position: fixed;
    top: 0;
    left: 0;
    width: var(--actify-button-size);
    height: var(--actify-button-size);
    z-index: var(--actify-z);
    display: block;
    overflow: visible;
    pointer-events: auto;
    color-scheme: light;
  }

  :host([data-theme="dark"]) {
    --actify-ink: #f1f5f9;
    --actify-paper: #111827;
    --actify-line: rgba(203, 213, 225, .16);
    --actify-muted: #a8b4c3;
    --actify-hover: rgba(226, 232, 240, .1);
    --actify-badge-ring: #111827;
    color-scheme: dark;
  }

  * { box-sizing: border-box; }

  .actify-launcher {
    position: absolute;
    width: var(--actify-button-size);
    height: var(--actify-button-size);
    left: 0;
    top: 0;
    z-index: var(--actify-z);
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 50%;
    color: #fff;
    background: var(--actify-primary);
    box-shadow:
      0 18px 38px rgba(14, 57, 49, .25),
      0 4px 12px rgba(14, 57, 49, .2),
      inset 0 0 0 1px rgba(255,255,255,.28);
    cursor: grab;
    touch-action: none;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    transition: box-shadow .22s ease, scale .22s ease, opacity .2s ease;
    pointer-events: auto;
  }

  .actify-launcher::before {
    content: '';
    position: absolute;
    inset: -6px;
    border: 1px solid color-mix(in srgb, var(--actify-primary) 35%, transparent);
    border-radius: inherit;
    opacity: .55;
    animation: actify-breathe 3.2s ease-in-out infinite;
  }

  .actify-launcher:hover {
    scale: 1.045;
    box-shadow:
      0 22px 44px rgba(14, 57, 49, .3),
      0 5px 14px rgba(14, 57, 49, .22),
      inset 0 0 0 1px rgba(255,255,255,.32);
  }

  .actify-launcher:focus-visible {
    outline: 3px solid color-mix(in srgb, var(--actify-primary) 34%, white);
    outline-offset: 4px;
  }

  .actify-launcher.is-dragging {
    cursor: grabbing;
    scale: 1.07;
    transition: none;
  }

  .actify-launcher.is-open {
    opacity: 0;
    scale: .82;
    pointer-events: none;
  }

  .actify-launcher svg {
    width: 32px;
    height: 32px;
    filter: drop-shadow(0 2px 2px rgba(0,0,0,.12));
  }

  .actify-badge {
    position: absolute;
    min-width: 20px;
    height: 20px;
    top: -3px;
    right: -3px;
    display: none;
    place-items: center;
    padding: 0 5px;
    border: 2px solid var(--actify-badge-ring);
    border-radius: 999px;
    background: #d94d3f;
    color: #fff;
    font: 700 10px/1 ui-sans-serif, system-ui, sans-serif;
  }

  .actify-badge.is-visible { display: grid; }

  .actify-panel {
    position: fixed;
    z-index: calc(var(--actify-z) - 1);
    display: grid;
    grid-template-rows: 48px minmax(0, 1fr);
    overflow: hidden;
    border: 1px solid var(--actify-line);
    border-radius: 22px;
    background: var(--actify-paper);
    box-shadow:
      0 34px 80px rgba(16, 35, 31, .22),
      0 10px 26px rgba(16, 35, 31, .12);
    opacity: 0;
    visibility: hidden;
    transform: translateY(12px) scale(.975);
    transform-origin: 90% 100%;
    transition: opacity .2s ease, transform .24s cubic-bezier(.22,.8,.28,1), visibility .2s,
      background-color .18s ease, border-color .18s ease;
    pointer-events: auto;
  }

  .actify-panel.is-open {
    opacity: 1;
    visibility: visible;
    transform: translateY(0) scale(1);
  }

  .actify-panel.is-interacting {
    transition: none;
    user-select: none;
  }

  .actify-panel-header {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 0 10px 0 16px;
    border-bottom: 1px solid var(--actify-line);
    background:
      linear-gradient(90deg, color-mix(in srgb, var(--actify-primary) 8%, var(--actify-paper)), var(--actify-paper) 65%);
    color: var(--actify-ink);
    cursor: grab;
    touch-action: none;
    user-select: none;
    transition: background-color .18s ease, border-color .18s ease, color .18s ease;
  }

  .actify-panel-header.is-dragging { cursor: grabbing; }

  .actify-panel-mark {
    width: 10px;
    height: 10px;
    flex: 0 0 auto;
    border: 2px solid rgba(255, 255, 255, .92);
    border-radius: 50%;
    background: #94a3b8;
    box-shadow: 0 0 0 4px rgba(148, 163, 184, .12);
    transition: background-color .18s ease, box-shadow .18s ease;
  }

  .actify-panel-mark[data-status="connected"] {
    background: #10b981;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, .12);
  }

  .actify-panel-mark[data-status="connecting"] {
    background: #f59e0b;
    box-shadow: 0 0 0 4px rgba(245, 158, 11, .14);
    animation: actify-status-pulse 1s ease-in-out infinite;
  }

  .actify-panel-mark[data-status="error"] {
    background: #ef4444;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, .12);
  }

  .actify-panel-title {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    color: var(--actify-ink);
    font: 720 13px/1.2 ui-rounded, "SF Pro Rounded", "PingFang SC", sans-serif;
    letter-spacing: .01em;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actify-panel-close {
    position: relative;
    z-index: 5;
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: var(--actify-muted);
    cursor: pointer;
    touch-action: manipulation;
    transition: background .16s ease, color .16s ease;
  }

  .actify-panel-close:hover {
    color: var(--actify-ink);
    background: var(--actify-hover);
  }

  .actify-panel-close:focus { outline: none; }

  .actify-panel-close:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--actify-primary) 55%, transparent);
    outline-offset: 2px;
  }

  .actify-panel-close svg { width: 19px; height: 19px; }

  .actify-resize-handle {
    position: absolute;
    z-index: 4;
    width: 22px;
    height: 22px;
    background: transparent;
    touch-action: none;
  }

  .actify-resize-handle[data-corner="nw"] {
    top: 0;
    left: 0;
    cursor: nwse-resize;
  }

  .actify-resize-handle[data-corner="ne"] {
    top: 0;
    right: 0;
    cursor: nesw-resize;
  }

  .actify-resize-handle[data-corner="sw"] {
    bottom: 0;
    left: 0;
    cursor: nesw-resize;
  }

  .actify-resize-handle[data-corner="se"] {
    right: 0;
    bottom: 0;
    cursor: nwse-resize;
  }

  .actify-frame {
    width: 100%;
    height: 100%;
    border: 0;
    background: var(--actify-paper);
  }

  @keyframes actify-breathe {
    0%, 100% { scale: .98; opacity: .32; }
    50% { scale: 1.08; opacity: .66; }
  }

  @keyframes actify-status-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: .48; }
  }

  @media (prefers-reduced-motion: reduce) {
    .actify-launcher, .actify-panel { transition: none; }
    .actify-launcher::before, .actify-panel-mark { animation: none; }
  }

  @media (max-width: 600px), (max-height: 560px) {
    .actify-panel {
      border-radius: 18px;
      transform-origin: 50% 100%;
    }

    .actify-panel-header {
      cursor: default;
      touch-action: auto;
    }

    .actify-resize-handle { display: none; }
  }
`

function isPlainObject(value) {
  if (!value || Object.prototype.toString.call(value) !== '[object Object]') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function sanitizeContextValue(value, depth = 0) {
  if (depth > 6) throw new Error('[Actify] context nesting cannot exceed 6 levels')
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (Array.isArray(value)) {
    return value.slice(0, 100).map((item) => sanitizeContextValue(item, depth + 1))
  }
  if (isPlainObject(value)) {
    const result = Object.create(null)
    Object.entries(value).forEach(([key, item]) => {
      if (!BLOCKED_CONTEXT_KEYS.has(key)) result[key] = sanitizeContextValue(item, depth + 1)
    })
    return result
  }
  return undefined
}

function sanitizeContext(context) {
  const safeContext = sanitizeContextValue(isPlainObject(context) ? context : {})
  const serialized = JSON.stringify(safeContext)
  if (new TextEncoder().encode(serialized).byteLength > MAX_CONTEXT_BYTES) {
    throw new Error(`[Actify] context cannot exceed ${MAX_CONTEXT_BYTES / 1024}KB`)
  }
  return JSON.parse(serialized)
}

// CLIENT_TOOL_HELPERS_START
function getSerializedByteLength(value) {
  return new TextEncoder().encode(value).byteLength
}

function createClientToolRegistrationId() {
  return globalThis.crypto?.randomUUID?.()
    || `sdk_tool_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`
}

function normalizeClientToolSchema(inputSchema) {
  if (inputSchema === undefined) return { type: 'object', properties: {} }
  if (typeof inputSchema !== 'string' && !isPlainObject(inputSchema)) {
    throw new Error('[Actify] client tool inputSchema must be a string or object')
  }

  let serialized
  try {
    serialized = typeof inputSchema === 'string' ? inputSchema : JSON.stringify(inputSchema)
  } catch {
    throw new Error('[Actify] client tool inputSchema must be JSON serializable')
  }
  if (typeof serialized !== 'string') {
    throw new Error('[Actify] client tool inputSchema must be JSON serializable')
  }
  if (getSerializedByteLength(serialized) > MAX_CLIENT_TOOL_SCHEMA_BYTES) {
    throw new Error(`[Actify] client tool inputSchema cannot exceed ${MAX_CLIENT_TOOL_SCHEMA_BYTES / 1024}KB`)
  }
  return typeof inputSchema === 'string' ? inputSchema : JSON.parse(serialized)
}

function normalizeClientToolDefinition(definition) {
  if (!isPlainObject(definition)) {
    throw new Error('[Actify] client tool definition must be an object')
  }

  const id = String(definition.id || '').trim()
  const name = String(definition.name || '').trim()
  const description = String(definition.description || '').trim()
  if (!id || id.length > MAX_CLIENT_TOOL_ID_LENGTH) {
    throw new Error(`[Actify] client tool id must be 1 to ${MAX_CLIENT_TOOL_ID_LENGTH} characters`)
  }
  if (!CLIENT_TOOL_NAME_PATTERN.test(name)) {
    throw new Error('[Actify] client tool name must start with a letter or underscore and contain at most 64 letters, digits, underscores, or hyphens')
  }
  if (!description || description.length > MAX_CLIENT_TOOL_DESCRIPTION_LENGTH) {
    throw new Error(`[Actify] client tool description must be 1 to ${MAX_CLIENT_TOOL_DESCRIPTION_LENGTH} characters`)
  }
  if (typeof definition.execute !== 'function') {
    throw new Error(`[Actify] client tool '${name}' requires an execute function`)
  }

  let timeoutMs
  if (definition.timeoutMs !== undefined) {
    timeoutMs = Number(definition.timeoutMs)
    if (!Number.isFinite(timeoutMs) || timeoutMs < MIN_CLIENT_TOOL_TIMEOUT_MS || timeoutMs > MAX_CLIENT_TOOL_TIMEOUT_MS) {
      throw new Error(`[Actify] client tool timeoutMs must be between ${MIN_CLIENT_TOOL_TIMEOUT_MS} and ${MAX_CLIENT_TOOL_TIMEOUT_MS}`)
    }
  }

  return {
    id,
    name,
    description,
    inputSchema: normalizeClientToolSchema(definition.inputSchema),
    ...(timeoutMs === undefined ? {} : { timeoutMs }),
    execute: definition.execute
  }
}

function getClientToolMetadata(tool) {
  const { execute, ...metadata } = tool
  return metadata
}

function normalizeClientToolResult(result) {
  if (result === undefined) return undefined

  let serialized
  try {
    serialized = typeof result === 'string' ? result : JSON.stringify(result)
  } catch {
    throw new Error('[Actify] client tool result must be JSON serializable')
  }
  if (typeof serialized !== 'string') {
    throw new Error('[Actify] client tool result must be JSON serializable')
  }
  if (getSerializedByteLength(serialized) > MAX_CLIENT_TOOL_RESULT_BYTES) {
    throw new Error(`[Actify] client tool result cannot exceed ${MAX_CLIENT_TOOL_RESULT_BYTES} bytes`)
  }
  return typeof result === 'string' ? result : JSON.parse(serialized)
}

function getSafeClientToolError(error) {
  const message = error instanceof Error ? error.message : String(error || 'Client tool execution failed.')
  return message.trim().slice(0, 1_000) || 'Client tool execution failed.'
}
// CLIENT_TOOL_HELPERS_END

function resolveClientUrl(options) {
  if (options.clientUrl) return new URL(options.clientUrl, window.location.href).origin
  const script = document.currentScript || [...document.scripts].find((item) => item.src.includes('actify-sdk'))
  if (script?.src) return new URL(script.src, window.location.href).origin
  return window.location.origin
}

class ActifyWidget {
  constructor(options = {}) {
    if (typeof options.appCode !== 'string' || !options.appCode.trim()) {
      throw new Error('[Actify] appCode is required')
    }

    const normalizedOptions = Object.fromEntries(
      Object.entries(options).filter(([, value]) => value !== undefined && value !== null && value !== '')
    )
    this.options = {
      title: 'Actify 智能助手',
      buttonLabel: '打开智能助手',
      primaryColor: DEFAULT_PRIMARY_COLOR,
      theme: 'auto',
      authMode: 'auto',
      panelWidth: DEFAULT_PANEL_WIDTH,
      panelHeight: DEFAULT_PANEL_HEIGHT,
      minPanelWidth: DEFAULT_PANEL_MIN_WIDTH,
      minPanelHeight: DEFAULT_PANEL_MIN_HEIGHT,
      buttonSize: DEFAULT_BUTTON_SIZE,
      margin: DEFAULT_MARGIN,
      zIndex: 2147483000,
      autoOpen: false,
      ...normalizedOptions
    }
    this.appCode = options.appCode.trim()
    this.clientUrl = resolveClientUrl(this.options)
    this.clientOrigin = new URL(this.clientUrl).origin
    this.context = sanitizeContext(options.context || {})
    const initialToken = normalizeTokenResult({
      token: options.token || '',
      expiresAt: options.tokenExpiresAt,
      expiresIn: options.tokenExpiresIn
    })
    this.token = initialToken.token
    this.tokenExpiresAt = initialToken.expiresAt
    this.tokenRevision = 0
    this.authMode = this.resolveAuthMode(this.token)
    this.handlers = new Map()
    this.destroyed = false
    this.opened = false
    this.ready = false
    this.handshakeId = ''
    this.dragState = null
    this.dragCleanup = null
    this.panelBounds = null
    this.panelInteractionState = null
    this.panelInteractionCleanup = null
    this.ignoreNextClick = false
    this.authRefreshAttempted = false
    this.authRefreshInFlight = false
    this.tokenRefreshTimer = null
    this.unreadCount = 0
    this.position = null
    this.clientTools = new Map()
    this.runningClientToolCalls = new Map()
    this.boundMessage = this.handleMessage.bind(this)
    this.boundResize = this.handleViewportResize.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundHostActivity = this.handleHostActivity.bind(this)

    this.mount()
  }

  mount() {
    this.host = document.createElement('div')
    this.host.dataset.actifySdk = this.appCode
    this.host.dataset.theme = this.resolveTheme()
    this.shadow = this.host.attachShadow({ mode: 'open' })
    this.shadow.innerHTML = `
      <style>${STYLES}</style>
      <button class="actify-launcher" type="button" aria-label="${this.escape(this.options.buttonLabel)}" aria-expanded="false">
        ${CHAT_ICON}
        <span class="actify-badge" role="status" aria-live="polite" aria-atomic="true"></span>
      </button>
      <section class="actify-panel" role="dialog" aria-modal="false" aria-label="${this.escape(this.options.title)}" aria-hidden="true">
        <header class="actify-panel-header">
          <span
            class="actify-panel-mark"
            data-status="disconnected"
            role="status"
            aria-label="WebSocket 未连接"
            title="WebSocket 未连接"
          ></span>
          <span class="actify-panel-title">${this.escape(this.options.title)}</span>
          <button class="actify-panel-close" type="button" aria-label="收起智能助手">${CLOSE_ICON}</button>
        </header>
        <iframe class="actify-frame" title="${this.escape(this.options.title)}" allow="clipboard-read; clipboard-write" referrerpolicy="strict-origin-when-cross-origin"></iframe>
        <span class="actify-resize-handle" data-corner="nw" aria-hidden="true"></span>
        <span class="actify-resize-handle" data-corner="ne" aria-hidden="true"></span>
        <span class="actify-resize-handle" data-corner="sw" aria-hidden="true"></span>
        <span class="actify-resize-handle" data-corner="se" aria-hidden="true"></span>
      </section>`

    this.shadow.host.style.setProperty('--actify-primary', this.options.primaryColor)
    this.shadow.host.style.setProperty('--actify-button-size', `${this.options.buttonSize}px`)
    this.shadow.host.style.setProperty('--actify-z', String(this.options.zIndex))

    this.launcher = this.shadow.querySelector('.actify-launcher')
    this.badge = this.shadow.querySelector('.actify-badge')
    this.panel = this.shadow.querySelector('.actify-panel')
    this.panelHeader = this.shadow.querySelector('.actify-panel-header')
    this.connectionMark = this.shadow.querySelector('.actify-panel-mark')
    this.closeButton = this.shadow.querySelector('.actify-panel-close')
    this.frame = this.shadow.querySelector('.actify-frame')
    this.resizeHandles = [...this.shadow.querySelectorAll('.actify-resize-handle')]

    document.body.appendChild(this.host)
    this.restorePosition()
    this.restorePanelBounds()
    this.renderPosition()
    this.positionPanel()

    this.launcher.addEventListener('pointerdown', (event) => this.startDrag(event))
    this.launcher.addEventListener('click', () => {
      if (this.ignoreNextClick) {
        this.ignoreNextClick = false
        return
      }
      this.toggle()
    })
    this.panelHeader.addEventListener('pointerdown', (event) => this.startPanelDrag(event))
    this.resizeHandles.forEach((handle) => {
      handle.addEventListener('pointerdown', (event) => this.startPanelResize(event, handle.dataset.corner))
    })
    this.closeButton.addEventListener('click', () => this.close())
    window.addEventListener('message', this.boundMessage)
    window.addEventListener('resize', this.boundResize, { passive: true })
    window.addEventListener('keydown', this.boundKeydown)
    window.addEventListener('focus', this.boundHostActivity)
    window.addEventListener('pageshow', this.boundHostActivity)
    document.addEventListener('visibilitychange', this.boundHostActivity)

    // Use a physical HTML entry so static deployments do not need an SPA
    // history-fallback rule for the iframe URL.
    const widgetUrl = new URL('/widget.html', `${this.clientUrl}/`)
    widgetUrl.searchParams.set('appCode', this.appCode)
    widgetUrl.searchParams.set('parentOrigin', window.location.origin)
    widgetUrl.searchParams.set('theme', this.resolveTheme())
    widgetUrl.searchParams.set('sdkVersion', SDK_VERSION)
    this.frame.src = widgetUrl.toString()

    if (this.options.autoOpen) this.open()
    this.emit('mounted', { version: SDK_VERSION, appCode: this.appCode })
  }

  escape(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;')
  }

  resolveTheme() {
    if (this.options.theme === 'dark' || this.options.theme === 'light') return this.options.theme
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  get storageKey() {
    return `actify:sdk:${this.appCode}:position:v1`
  }

  get panelStorageKey() {
    return `actify:sdk:${this.appCode}:panel:v1`
  }

  defaultPosition() {
    const size = this.options.buttonSize
    const margin = this.options.margin
    return {
      x: Math.max(margin, window.innerWidth - size - margin),
      y: Math.max(margin, window.innerHeight - size - margin)
    }
  }

  restorePosition() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null')
      this.position = saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)
        ? this.clampPosition(saved)
        : this.defaultPosition()
    } catch {
      this.position = this.defaultPosition()
    }
  }

  savePosition() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.position))
    } catch {
      // Storage can be disabled by the host. The widget still works for this page load.
    }
  }

  clampPosition(position) {
    const margin = Math.min(this.options.margin, 12)
    const size = this.options.buttonSize
    return {
      x: Math.min(Math.max(margin, Number(position.x) || margin), Math.max(margin, window.innerWidth - size - margin)),
      y: Math.min(Math.max(margin, Number(position.y) || margin), Math.max(margin, window.innerHeight - size - margin))
    }
  }

  renderPosition() {
    if (!this.position || !this.launcher) return
    this.host.style.left = `${Math.round(this.position.x)}px`
    this.host.style.top = `${Math.round(this.position.y)}px`
  }

  startDrag(event) {
    if (event.button !== 0 || this.destroyed) return
    event.preventDefault()
    try {
      this.launcher.setPointerCapture(event.pointerId)
    } catch {
      // Some embedded webviews do not implement pointer capture.
    }
    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: this.position.x,
      originY: this.position.y,
      moved: false
    }
    this.launcher.classList.add('is-dragging')

    const move = (moveEvent) => {
      if (!this.dragState || moveEvent.pointerId !== this.dragState.pointerId) return
      const dx = moveEvent.clientX - this.dragState.startX
      const dy = moveEvent.clientY - this.dragState.startY
      if (Math.hypot(dx, dy) > 5) this.dragState.moved = true
      this.position = this.clampPosition({
        x: this.dragState.originX + dx,
        y: this.dragState.originY + dy
      })
      this.renderPosition()
      if (this.opened) this.positionPanel()
    }

    const cleanup = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
      this.dragCleanup = null
    }

    const end = (endEvent) => {
      if (!this.dragState || endEvent.pointerId !== this.dragState.pointerId) return
      const wasMoved = this.dragState.moved
      this.dragState = null
      this.launcher.classList.remove('is-dragging')
      cleanup()
      this.savePosition()
      this.positionPanel()
      this.ignoreNextClick = wasMoved
    }

    this.dragCleanup?.()
    this.dragCleanup = cleanup
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
  }

  isCompactViewport() {
    return window.innerWidth <= 600 || window.innerHeight <= 560
  }

  restorePanelBounds() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(this.panelStorageKey) || 'null')
      this.panelBounds = saved
        && Number.isFinite(saved.left)
        && Number.isFinite(saved.top)
        && Number.isFinite(saved.width)
        && Number.isFinite(saved.height)
        ? saved
        : null
    } catch {
      this.panelBounds = null
    }
  }

  savePanelBounds() {
    if (!this.panelBounds || this.isCompactViewport()) return
    try {
      window.localStorage.setItem(this.panelStorageKey, JSON.stringify(this.panelBounds))
    } catch {
      // Storage can be disabled by the host. The panel still works for this page load.
    }
  }

  getPanelSizeLimits() {
    const gap = 14
    const maxWidth = Math.max(1, window.innerWidth - gap * 2)
    const maxHeight = Math.max(1, window.innerHeight - gap * 2)
    return {
      gap,
      maxWidth,
      maxHeight,
      minWidth: Math.min(maxWidth, Math.max(DEFAULT_PANEL_MIN_WIDTH, Number(this.options.minPanelWidth) || DEFAULT_PANEL_MIN_WIDTH)),
      minHeight: Math.min(maxHeight, Math.max(DEFAULT_PANEL_MIN_HEIGHT, Number(this.options.minPanelHeight) || DEFAULT_PANEL_MIN_HEIGHT))
    }
  }

  createDefaultPanelBounds() {
    const { gap, maxWidth, maxHeight, minWidth, minHeight } = this.getPanelSizeLimits()
    const width = Math.min(maxWidth, Math.max(minWidth, Number(this.options.panelWidth) || DEFAULT_PANEL_WIDTH))
    const height = Math.min(maxHeight, Math.max(minHeight, Number(this.options.panelHeight) || DEFAULT_PANEL_HEIGHT))
    const buttonCenterX = this.position.x + this.options.buttonSize / 2
    const buttonCenterY = this.position.y + this.options.buttonSize / 2
    const left = buttonCenterX > window.innerWidth / 2
      ? this.position.x + this.options.buttonSize - width
      : this.position.x
    const top = buttonCenterY > window.innerHeight / 2
      ? this.position.y - height - gap
      : this.position.y + this.options.buttonSize + gap
    return this.clampPanelBounds({ left, top, width, height })
  }

  clampPanelBounds(bounds) {
    const { gap, maxWidth, maxHeight, minWidth, minHeight } = this.getPanelSizeLimits()
    const requestedWidth = Number(bounds?.width)
    const requestedHeight = Number(bounds?.height)
    const width = Math.min(maxWidth, Math.max(minWidth, Number.isFinite(requestedWidth) ? requestedWidth : this.options.panelWidth))
    const height = Math.min(maxHeight, Math.max(minHeight, Number.isFinite(requestedHeight) ? requestedHeight : this.options.panelHeight))
    const requestedLeft = Number(bounds?.left)
    const requestedTop = Number(bounds?.top)
    return {
      left: Math.min(Math.max(gap, Number.isFinite(requestedLeft) ? requestedLeft : gap), window.innerWidth - width - gap),
      top: Math.min(Math.max(gap, Number.isFinite(requestedTop) ? requestedTop : gap), window.innerHeight - height - gap),
      width,
      height
    }
  }

  renderPanelBounds(bounds) {
    if (!this.panel || !bounds) return
    this.panel.style.width = `${Math.round(bounds.width)}px`
    this.panel.style.height = `${Math.round(bounds.height)}px`
    this.panel.style.left = `${Math.round(bounds.left)}px`
    this.panel.style.top = `${Math.round(bounds.top)}px`
  }

  positionPanel() {
    if (!this.panel || !this.position) return
    if (this.isCompactViewport()) {
      const gap = 10
      this.renderPanelBounds({
        left: gap,
        top: gap,
        width: Math.max(1, window.innerWidth - gap * 2),
        height: Math.max(1, window.innerHeight - gap * 2)
      })
      return
    }

    this.panelBounds = this.clampPanelBounds(this.panelBounds || this.createDefaultPanelBounds())
    this.renderPanelBounds(this.panelBounds)
  }

  resizePanelBounds(origin, corner, dx, dy) {
    const { gap, minWidth, minHeight } = this.getPanelSizeLimits()
    let left = origin.left
    let top = origin.top
    let right = origin.left + origin.width
    let bottom = origin.top + origin.height

    if (corner.includes('w')) {
      left = Math.min(Math.max(gap, origin.left + dx), right - minWidth)
    } else {
      right = Math.max(Math.min(window.innerWidth - gap, right + dx), left + minWidth)
    }

    if (corner.includes('n')) {
      top = Math.min(Math.max(gap, origin.top + dy), bottom - minHeight)
    } else {
      bottom = Math.max(Math.min(window.innerHeight - gap, bottom + dy), top + minHeight)
    }

    return { left, top, width: right - left, height: bottom - top }
  }

  startPanelDrag(event) {
    if (event.target.closest('.actify-panel-close')) return
    this.startPanelInteraction(event, 'move')
  }

  startPanelResize(event, corner) {
    this.startPanelInteraction(event, 'resize', corner)
  }

  startPanelInteraction(event, type, corner = '') {
    if (event.button !== 0 || this.destroyed || this.isCompactViewport()) return
    event.preventDefault()
    event.stopPropagation()
    const source = event.currentTarget
    try {
      source.setPointerCapture(event.pointerId)
    } catch {
      // Some embedded webviews do not implement pointer capture.
    }

    this.panelInteractionCleanup?.()
    this.panelBounds = this.clampPanelBounds(this.panelBounds || this.createDefaultPanelBounds())
    this.panelInteractionState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: { ...this.panelBounds },
      type,
      corner
    }
    this.panel.classList.add('is-interacting')
    if (type === 'move') this.panelHeader.classList.add('is-dragging')

    const move = (moveEvent) => {
      const state = this.panelInteractionState
      if (!state || moveEvent.pointerId !== state.pointerId) return
      const dx = moveEvent.clientX - state.startX
      const dy = moveEvent.clientY - state.startY
      this.panelBounds = state.type === 'move'
        ? this.clampPanelBounds({ ...state.origin, left: state.origin.left + dx, top: state.origin.top + dy })
        : this.resizePanelBounds(state.origin, state.corner, dx, dy)
      this.renderPanelBounds(this.panelBounds)
    }

    const cleanup = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
      this.panel.classList.remove('is-interacting')
      this.panelHeader.classList.remove('is-dragging')
      this.panelInteractionCleanup = null
    }

    const end = (endEvent) => {
      if (!this.panelInteractionState || endEvent.pointerId !== this.panelInteractionState.pointerId) return
      const completedType = this.panelInteractionState.type
      this.panelInteractionState = null
      cleanup()
      this.savePanelBounds()
      this.emit(completedType === 'move' ? 'panelMove' : 'panelResize', { ...this.panelBounds })
    }

    this.panelInteractionCleanup = cleanup
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
  }

  handleViewportResize() {
    if (this.destroyed) return
    this.position = this.clampPosition(this.position || this.defaultPosition())
    this.renderPosition()
    this.positionPanel()
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.opened) this.close()
  }

  hasTokenProvider() {
    return typeof this.options.getToken === 'function' || Boolean(this.options.authUrl)
  }

  async requestProviderToken() {
    if (typeof this.options.getToken === 'function') {
      const result = await this.options.getToken()
      return normalizeTokenResult(result)
    }
    if (this.options.authUrl) {
      const response = await window.fetch(this.options.authUrl, {
        method: this.options.authMethod || 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json', ...(this.options.authHeaders || {}) }
      })
      if (!response.ok) throw new Error(`[Actify] token endpoint returned ${response.status}`)
      const result = await response.json()
      return normalizeTokenResult(result)
    }
    return normalizeTokenResult('')
  }

  applyTokenResult(result) {
    const normalized = normalizeTokenResult(result)
    this.token = normalized.token
    this.tokenExpiresAt = normalized.expiresAt
    this.tokenRevision += 1
    this.authMode = this.resolveAuthMode(this.token)
    return normalized
  }

  async resolveToken() {
    if (this.token) return this.token
    if (!this.hasTokenProvider()) return ''
    const revision = this.tokenRevision
    const result = await this.requestProviderToken()
    if (!this.destroyed && this.tokenRevision === revision) this.applyTokenResult(result)
    return this.token
  }

  clearTokenRefreshTimer() {
    if (!this.tokenRefreshTimer) return
    window.clearTimeout(this.tokenRefreshTimer)
    this.tokenRefreshTimer = null
  }

  scheduleTokenRefresh(delayOverride, allowWithoutToken = false) {
    this.clearTokenRefreshTimer()
    if (this.destroyed || (!this.token && !allowWithoutToken) || !this.hasTokenProvider()) return
    const delay = Number.isFinite(delayOverride)
      ? Math.max(Number(delayOverride), MIN_TOKEN_REFRESH_DELAY_MS)
      : getTokenRefreshDelay({
          expiresAt: this.tokenExpiresAt,
          leewaySeconds: this.options.tokenRefreshLeewaySeconds,
          intervalSeconds: this.options.tokenRefreshIntervalSeconds
        })
    this.tokenRefreshTimer = window.setTimeout(() => {
      this.tokenRefreshTimer = null
      void this.refreshToken('scheduled')
    }, delay)
  }

  async refreshToken(reason = 'manual') {
    if (this.destroyed || !this.hasTokenProvider()) return this.token
    if (this.authRefreshInFlight) return this.token

    this.authRefreshInFlight = true
    try {
      const previousToken = this.token
      const revision = this.tokenRevision
      const result = await this.requestProviderToken()
      if (this.destroyed) return this.token
      if (this.tokenRevision !== revision) {
        this.scheduleTokenRefresh()
        return this.token
      }
      if (!result.token) throw new Error('[Actify] token provider returned an empty token')
      if (result.expiresAt && result.expiresAt <= Date.now()) {
        throw new Error('[Actify] token provider returned an expired token')
      }
      this.applyTokenResult(result)
      this.authRefreshAttempted = false
      if (this.ready && this.token !== previousToken) {
        this.post('auth-refresh', { token: this.token, authMode: this.authMode })
      }
      // A provider may legitimately return the same opaque token. Schedule from
      // its current expiry/interval instead of treating equality as a failure.
      this.scheduleTokenRefresh()
      this.emit('tokenRefresh', {
        appCode: this.appCode,
        reason,
        changed: this.token !== previousToken,
        expiresAt: this.tokenExpiresAt ? new Date(this.tokenExpiresAt).toISOString() : null
      })
      return this.token
    } catch (error) {
      if (this.destroyed) return this.token
      this.emit('error', safeAuthErrorEvent(
        'AUTH_TOKEN_REFRESH_FAILED',
        error,
        '无法续签登录状态，SDK 将自动重试'
      ))
      this.scheduleTokenRefresh(parsePositiveSeconds(
        this.options.tokenRefreshRetrySeconds,
        DEFAULT_TOKEN_REFRESH_RETRY_SECONDS,
        1,
        5 * 60
      ) * 1000, !this.token)
      return this.token
    } finally {
      this.authRefreshInFlight = false
    }
  }

  handleHostActivity() {
    if (this.destroyed || document.visibilityState === 'hidden' || !this.token || !this.hasTokenProvider()) return
    // Opaque tokens have no absolute deadline. Keep the existing interval instead
    // of pushing it back every time the host window regains focus.
    if (!this.tokenExpiresAt) {
      if (!this.tokenRefreshTimer) this.scheduleTokenRefresh()
      return
    }
    const delay = getTokenRefreshDelay({
      expiresAt: this.tokenExpiresAt,
      leewaySeconds: this.options.tokenRefreshLeewaySeconds,
      intervalSeconds: this.options.tokenRefreshIntervalSeconds
    })
    if (delay <= MIN_TOKEN_REFRESH_DELAY_MS) {
      void this.refreshToken('host-active')
      return
    }
    this.scheduleTokenRefresh(delay)
  }

  async sendInitialization() {
    let token = ''
    const revision = this.tokenRevision
    try {
      token = await this.resolveToken()
    } catch (error) {
      // A host-driven setToken/auth update that happened while the provider was
      // pending owns the newer state. Complete the first iframe handshake with
      // that current token, but do not emit/retry the stale provider failure.
      if (this.destroyed) return
      if (this.tokenRevision !== revision) {
        token = this.token
      } else {
        this.emit('error', safeAuthErrorEvent(
          'AUTH_TOKEN_FAILED',
          error,
          '暂时无法获取登录状态，SDK 将自动重试'
        ))
        this.scheduleTokenRefresh(parsePositiveSeconds(
          this.options.tokenRefreshRetrySeconds,
          DEFAULT_TOKEN_REFRESH_RETRY_SECONDS,
          1,
          5 * 60
        ) * 1000, true)
      }
    }
    const authMode = this.resolveAuthMode(token)
    this.authMode = authMode
    if (token) this.scheduleTokenRefresh()
    this.post('init', {
      appCode: this.appCode,
      token,
      authMode,
      context: this.context,
      theme: this.resolveTheme(),
      sdkVersion: SDK_VERSION
    })
    if (!token && authMode === 'sso') this.emit('authRequired', { appCode: this.appCode })
  }

  resolveAuthMode(token = '') {
    if (this.options.authMode === 'sso' || this.options.authMode === 'account') {
      return this.options.authMode
    }
    return token || this.options.token || typeof this.options.getToken === 'function' || this.options.authUrl || this.authMode === 'sso'
      ? 'sso'
      : 'account'
  }

  handleMessage(event) {
    if (this.destroyed || event.source !== this.frame?.contentWindow || event.origin !== this.clientOrigin) return
    const { type, payload } = event.data || {}
    if (typeof type !== 'string' || !type.startsWith(MESSAGE_PREFIX)) return
    const eventName = type.slice(MESSAGE_PREFIX.length)

    if (eventName === 'ready') {
      const handshakeId = String(payload?.handshakeId || 'legacy')
      if (this.handshakeId === handshakeId) return
      this.handshakeId = handshakeId
      this.ready = true
      this.sendInitialization()
      this.syncClientTools()
      return
    }
    if (eventName === 'client-tool-execute') {
      void this.executeClientTool(payload)
      return
    }
    if (eventName === 'close') {
      this.close()
      return
    }
    if (eventName === 'config') {
      this.applyAppConfig(payload)
    }
    if (eventName === 'unread') {
      this.setUnreadCount(payload?.count || 0)
    }
    if (eventName === 'connection-status') {
      this.applyConnectionStatus(payload)
    }
    if ((eventName === 'initialized' && payload?.authenticated) || eventName === 'authenticated') {
      this.authRefreshAttempted = false
    }
    if (
      eventName === 'auth-required'
      && (typeof this.options.getToken === 'function' || this.options.authUrl)
      && !this.authRefreshAttempted
      && !this.authRefreshInFlight
    ) {
      this.authRefreshAttempted = true
      this.authRefreshInFlight = true
      this.applyTokenResult('')
      this.clearTokenRefreshTimer()
      Promise.resolve(this.sendInitialization()).finally(() => {
        this.authRefreshInFlight = false
      })
    }
    if (eventName === 'auth-required') {
      this.emit('authRequired', payload || {})
    }
    this.emit(eventName, payload || {})
  }

  syncClientTools() {
    if (!this.ready || this.destroyed) return false
    return this.post('client-tools-sync', {
      tools: [...this.clientTools.values()].map(getClientToolMetadata)
    })
  }

  registerTool(definition) {
    if (this.destroyed) throw new Error('[Actify] cannot register a client tool after destroy()')
    const tool = {
      ...normalizeClientToolDefinition(definition),
      registrationId: createClientToolRegistrationId()
    }
    const conflictingTool = [...this.clientTools.values()]
      .find(item => item.id !== tool.id && item.name === tool.name)
    if (conflictingTool) {
      throw new Error(`[Actify] client tool name is already registered: ${tool.name}`)
    }

    const previous = this.clientTools.get(tool.id)
    if (previous) {
      this.cancelClientToolExecutions(tool.id, `Client tool '${previous.name}' was replaced.`)
    }
    this.clientTools.set(tool.id, tool)
    this.syncClientTools()

    return () => {
      if (this.clientTools.get(tool.id) !== tool) return false
      return this.unregisterTool(tool.id)
    }
  }

  unregisterTool(toolId) {
    const id = String(toolId || '').trim()
    const tool = this.clientTools.get(id)
    if (!tool) return false

    this.clientTools.delete(id)
    this.cancelClientToolExecutions(id, `Client tool '${tool.name}' was unregistered.`)
    this.syncClientTools()
    return true
  }

  cancelClientToolExecutions(toolId, reason) {
    for (const pending of this.runningClientToolCalls.values()) {
      if (toolId && pending.toolId !== toolId) continue
      pending.controller.abort(new Error(reason || 'Client tool execution was cancelled.'))
    }
  }

  async executeClientTool(payload = {}) {
    const requestId = String(payload.requestId || '').trim()
    const toolId = String(payload.toolId || '').trim()
    if (!requestId || !toolId || this.runningClientToolCalls.has(requestId)) return

    const tool = this.clientTools.get(toolId)
    if (!tool) {
      this.post('client-tool-result', {
        requestId,
        toolId,
        success: false,
        error: `Unknown or unregistered client tool: ${toolId}`
      })
      return
    }
    const registrationId = String(payload.registrationId || '').trim()
    if (registrationId !== tool.registrationId || (payload.toolName && payload.toolName !== tool.name)) {
      this.post('client-tool-result', {
        requestId,
        toolId,
        success: false,
        error: `Client tool '${tool.name}' registration was replaced.`
      })
      return
    }

    const requestedTimeoutMs = Number(payload.timeoutMs ?? tool.timeoutMs ?? DEFAULT_CLIENT_TOOL_TIMEOUT_MS)
    const timeoutMs = Number.isFinite(requestedTimeoutMs)
      ? Math.min(Math.max(requestedTimeoutMs, MIN_CLIENT_TOOL_TIMEOUT_MS), MAX_CLIENT_TOOL_TIMEOUT_MS)
      : DEFAULT_CLIENT_TOOL_TIMEOUT_MS
    const controller = new AbortController()
    let abortHandler
    const aborted = new Promise((_resolve, reject) => {
      abortHandler = () => reject(
        controller.signal.reason instanceof Error
          ? controller.signal.reason
          : new Error('Client tool execution was cancelled.')
      )
      controller.signal.addEventListener('abort', abortHandler, { once: true })
    })
    const timer = window.setTimeout(() => {
      controller.abort(new Error(`Client tool '${tool.name}' timed out after ${Math.ceil(timeoutMs / 1_000)} seconds.`))
    }, timeoutMs)

    this.runningClientToolCalls.set(requestId, {
      toolId,
      controller,
      timer
    })

    try {
      const args = payload.arguments && typeof payload.arguments === 'object'
        ? payload.arguments
        : {}
      const execution = Promise.resolve().then(() => tool.execute(args, {
        signal: controller.signal,
        requestId,
        toolId,
        toolName: tool.name
      }))
      const result = normalizeClientToolResult(await Promise.race([execution, aborted]))
      this.post('client-tool-result', {
        requestId,
        toolId,
        success: true,
        result
      })
    } catch (error) {
      this.post('client-tool-result', {
        requestId,
        toolId,
        success: false,
        error: getSafeClientToolError(error)
      })
    } finally {
      window.clearTimeout(timer)
      controller.signal.removeEventListener('abort', abortHandler)
      this.runningClientToolCalls.delete(requestId)
    }
  }

  applyAppConfig(config = {}) {
    if (String(config.appCode || '') !== this.appCode) return
    const themeColor = String(config.themeColor || '').trim()
    if (!themeColor || themeColor.length > 64 || !globalThis.CSS?.supports?.('color', themeColor)) return
    this.options.primaryColor = themeColor
    this.shadow?.host.style.setProperty('--actify-primary', themeColor)
  }

  applyConnectionStatus(connection = {}) {
    if (!this.connectionMark) return
    const allowedStatuses = new Set(['connected', 'connecting', 'disconnected', 'error'])
    const status = allowedStatuses.has(connection.status) ? connection.status : 'disconnected'
    const defaultLabels = {
      connected: 'WebSocket 已连接',
      connecting: 'WebSocket 连接中',
      disconnected: 'WebSocket 未连接',
      error: 'WebSocket 连接异常'
    }
    const label = typeof connection.label === 'string' && connection.label.trim()
      ? connection.label.trim().slice(0, 80)
      : defaultLabels[status]

    this.connectionMark.dataset.status = status
    this.connectionMark.setAttribute('aria-label', label)
    this.connectionMark.setAttribute('title', label)
  }

  post(action, payload = {}) {
    if (!this.frame?.contentWindow || this.destroyed) return false
    try {
      this.frame.contentWindow.postMessage({ type: `${MESSAGE_PREFIX}${action}`, payload }, this.clientOrigin)
      return true
    } catch {
      return false
    }
  }

  open() {
    if (this.destroyed || this.opened) return this
    this.handleHostActivity()
    this.opened = true
    this.positionPanel()
    this.panel.classList.add('is-open')
    this.panel.setAttribute('aria-hidden', 'false')
    this.launcher.classList.add('is-open')
    this.launcher.setAttribute('aria-expanded', 'true')
    this.renderUnreadBadge()
    this.post('visibility', { open: true })
    this.emit('open', {})
    return this
  }

  close() {
    if (this.destroyed || !this.opened) return this
    this.opened = false
    this.panel.classList.remove('is-open')
    this.panel.setAttribute('aria-hidden', 'true')
    this.launcher.classList.remove('is-open')
    this.launcher.setAttribute('aria-expanded', 'false')
    this.renderUnreadBadge()
    this.post('visibility', { open: false })
    this.launcher.focus({ preventScroll: true })
    this.emit('close', {})
    return this
  }

  toggle() {
    return this.opened ? this.close() : this.open()
  }

  setContext(context) {
    this.context = sanitizeContext(context)
    this.post('context', { context: this.context, mode: 'replace' })
    this.emit('contextChange', { context: this.context })
    return this
  }

  patchContext(partialContext) {
    if (!isPlainObject(partialContext)) throw new Error('[Actify] context patch must be an object')
    return this.setContext({ ...this.context, ...partialContext })
  }

  setToken(token) {
    this.applyTokenResult(token)
    this.authRefreshAttempted = false
    if (this.token) this.scheduleTokenRefresh()
    else this.clearTokenRefreshTimer()
    this.post('auth', { token: this.token, authMode: this.authMode })
    return this
  }

  setTheme(theme) {
    if (!['light', 'dark', 'auto'].includes(theme)) {
      throw new Error('[Actify] theme must be light, dark or auto')
    }
    this.options.theme = theme
    const resolvedTheme = this.resolveTheme()
    if (this.host) this.host.dataset.theme = resolvedTheme
    this.post('theme', { theme: resolvedTheme })
    this.emit('themeChange', { theme: resolvedTheme, mode: theme })
    return this
  }

  selectAgent(agentId) {
    if (agentId) this.post('select-agent', { agentId: String(agentId) })
    return this
  }

  newSession() {
    this.post('new-session')
    return this
  }

  setView(view) {
    const nextView = view === 'workspace' ? 'workspace' : 'chat'
    this.post('view', { view: nextView })
    this.open()
    return this
  }

  openWorkspace() {
    return this.setView('workspace')
  }

  openChat() {
    return this.setView('chat')
  }

  setUnreadCount(count) {
    this.unreadCount = Math.max(0, Math.floor(Number(count) || 0))
    this.renderUnreadBadge()
  }

  renderUnreadBadge() {
    const value = this.unreadCount
    this.badge.textContent = value > 99 ? '99+' : String(value)
    this.badge.setAttribute('aria-label', value > 0 ? `${value} 条未读消息` : '暂无未读消息')
    this.badge.classList.toggle('is-visible', value > 0 && !this.opened)
  }

  on(eventName, handler) {
    if (typeof handler !== 'function') return () => {}
    if (!this.handlers.has(eventName)) this.handlers.set(eventName, new Set())
    this.handlers.get(eventName).add(handler)
    return () => this.off(eventName, handler)
  }

  off(eventName, handler) {
    this.handlers.get(eventName)?.delete(handler)
    return this
  }

  emit(eventName, payload) {
    this.handlers.get(eventName)?.forEach((handler) => {
      try {
        handler(payload)
      } catch (error) {
        console.error(`[Actify] ${eventName} handler failed`, error)
      }
    })
    window.dispatchEvent(new CustomEvent(`actify:${eventName}`, { detail: payload }))
  }

  destroy() {
    if (this.destroyed) return
    this.post('client-tools-sync', { tools: [] })
    this.cancelClientToolExecutions('', 'Actify widget was destroyed.')
    this.clientTools.clear()
    this.destroyed = true
    this.dragState = null
    this.dragCleanup?.()
    this.panelInteractionState = null
    this.panelInteractionCleanup?.()
    this.clearTokenRefreshTimer()
    window.removeEventListener('message', this.boundMessage)
    window.removeEventListener('resize', this.boundResize)
    window.removeEventListener('keydown', this.boundKeydown)
    window.removeEventListener('focus', this.boundHostActivity)
    window.removeEventListener('pageshow', this.boundHostActivity)
    document.removeEventListener('visibilitychange', this.boundHostActivity)
    this.handlers.clear()
    this.host?.remove()
  }
}

let activeWidget = null

const Actify = {
  version: SDK_VERSION,
  init(options) {
    activeWidget?.destroy()
    activeWidget = new ActifyWidget(options)
    return activeWidget
  },
  get instance() {
    return activeWidget
  },
  open() { return activeWidget?.open() },
  close() { return activeWidget?.close() },
  toggle() { return activeWidget?.toggle() },
  setContext(context) { return activeWidget?.setContext(context) },
  patchContext(context) { return activeWidget?.patchContext(context) },
  setToken(token) { return activeWidget?.setToken(token) },
  setTheme(theme) { return activeWidget?.setTheme(theme) },
  selectAgent(agentId) { return activeWidget?.selectAgent(agentId) },
  newSession() { return activeWidget?.newSession() },
  openWorkspace() { return activeWidget?.openWorkspace() },
  openChat() { return activeWidget?.openChat() },
  registerTool(definition) {
    if (!activeWidget) throw new Error('[Actify] init() must be called before registerTool()')
    return activeWidget.registerTool(definition)
  },
  unregisterTool(toolId) { return activeWidget?.unregisterTool(toolId) || false },
  on(eventName, handler) { return activeWidget?.on(eventName, handler) },
  off(eventName, handler) { return activeWidget?.off(eventName, handler) },
  destroy() {
    activeWidget?.destroy()
    activeWidget = null
  }
}

if (typeof window !== 'undefined') {
  window.Actify = Actify
  const autoScript = document.currentScript
  const appCode = autoScript?.dataset?.appCode
  if (appCode && autoScript.dataset.manual !== 'true') {
    const start = () => Actify.init({
      appCode,
      clientUrl: autoScript.dataset.clientUrl,
      authUrl: autoScript.dataset.authUrl,
      title: autoScript.dataset.title || undefined,
      primaryColor: autoScript.dataset.primaryColor || undefined,
      theme: autoScript.dataset.theme || undefined,
      authMode: autoScript.dataset.authMode || undefined,
      autoOpen: autoScript.dataset.autoOpen === 'true'
    })
    if (document.body) start()
    else document.addEventListener('DOMContentLoaded', start, { once: true })
  }
}
